import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { UserNoteGet } from 'api/models/UserNoteGet';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { AttioNotesService } from 'dashboard409a/services/attio/AttioNotesService';
import { CreateNoteRequest } from 'dashboard409a/services/attio.types';
import { Note } from 'dashboard409a/services/models.types';

export interface UseNoteHandlerProps {
  onSuccess?: (id: any, noteContent: string, createdNote?: UserNoteGet) => void;
}

export const useNoteHandler = ({ onSuccess }: UseNoteHandlerProps = {}) => {
  const [noteError, setNoteError] = useState<string | null>(null);
  const notesService = new AttioNotesService();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Mutation for creating/updating notes
  const noteMutation = useMutation({
    mutationFn: async ({
      request,
      hasExistingNote,
      existingNoteId,
    }: {
      request: CreateNoteRequest;
      hasExistingNote: boolean;
      existingNoteId?: string;
    }) => notesService.createOrUpdateNote(request, hasExistingNote, existingNoteId),
    onSuccess: (result: UserNoteGet, variables) => {
      // If this is a new note (not an update), update the React Query cache
      if (variables.request.attio_valuation_id && !variables.hasExistingNote && result.attio_valuation_id) {
        const valuationId = result.attio_valuation_id;
        
        // Convert the UserNoteGet to our Note type
        const note: Note = {
          id: result.id || 0,
          content: result.content || '',
          author_id: result.author || 0,
          author_email: '', // Not provided in the response
          author_name: '', // Not provided in the response
          created_at: result.created_at || new Date().toISOString(),
          updated_at: result.updated_at || new Date().toISOString(),
        };

        // Update the individual note cache
        queryClient.setQueryData([AttioQueryKeys.BULK_NOTES, valuationId], note);

        // Invalidate the bulk notes query to refetch
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.BULK_NOTES],
        });
      }
    },
  });

  const handleNoteSave = async (
    id: string | number,
    noteContent: string,
    currentNote: string | undefined,
    request: CreateNoteRequest,
    hasExistingNote: boolean,
    existingNoteId?: string
  ) => {
    setNoteError(null);

    try {
      const result = await noteMutation.mutateAsync({
        request,
        hasExistingNote,
        existingNoteId,
      });
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(id, noteContent, result);
      }
      
      enqueueSnackbar('Note saved successfully!', { variant: 'success' });
    } catch (err: unknown) {
      // Error handling
      let errorMessage = 'Failed to save note. Please try again.';
      if (err && typeof err === 'object' && 'response' in err) {
        const errorResponse = err as { response?: { data?: { message?: string } } };
        if (errorResponse.response?.data?.message) {
          errorMessage = errorResponse.response.data.message;
        }
      } else if (err && typeof err === 'object' && 'message' in err) {
        const errorWithMessage = err as { message: string };
        errorMessage = errorWithMessage.message;
      }
      
      setNoteError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return {
    isNoteLoading: noteMutation.isPending,
    noteError,
    handleNoteSave,
  };
};
